import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LOGO_URL, NAV_LINKS, CONTACT } from "@/lib/company";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (link) => {
    setOpen(false);
    if (link.external) {
      navigate(link.href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + link.href);
      return;
    }
    document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[#e2e8f0] shadow-sm"
          : "bg-white/80 backdrop-blur-sm"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 lg:h-28 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Great Solutions US · Tu aliado para crecer en Estados Unidos" className="h-16 lg:h-20 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => handleClick(l)}
              className="text-sm font-medium text-[#0f172a] hover:text-[#0B2D5C] transition-colors relative group"
              data-testid={`nav-${l.href.replace(/[#/]/g, "")}`}
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C89B2C] group-hover:w-full transition-all"></span>
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phone1Tel}`}
            className="flex items-center gap-2 text-sm font-medium text-[#0f172a] hover:text-[#0B2D5C] transition-colors"
            data-testid="navbar-phone"
          >
            <Phone size={16} strokeWidth={1.75} />
            {CONTACT.phone1}
          </a>
          <Button
            onClick={() => handleClick({ href: "#contacto" })}
            className="bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm px-5"
            data-testid="navbar-cta"
          >
            Agenda tu cita
          </Button>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#0f172a]"
          data-testid="navbar-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#e2e8f0] bg-white" data-testid="navbar-mobile">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => handleClick(l)}
                className="text-left py-3 text-[#0f172a] font-medium border-b border-[#e2e8f0]"
                data-testid={`nav-mobile-${l.href.replace(/[#/]/g, "")}`}
              >
                {l.label}
              </button>
            ))}
            <Button
              onClick={() => handleClick({ href: "#contacto" })}
              className="mt-4 bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm"
              data-testid="navbar-mobile-cta"
            >
              Agenda tu cita
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
